import React, { useCallback, useEffect, useState } from 'react';
import Get from '../../api/internal/Get';
import {
  getCurrentWeekDates,
  formatDate,
  buildScheduleQueryString,
} from './scheduleHelpers';
import WeeklyCalendar from './WeeklyCalendar';
import classes from './Schedule.module.css';
import { PageLoadingSpinner } from '../../helpers/loadingSpinner/LoadingSpinner';
import OptionList from '../../helpers/options/OptionList';
import Accordion from '../../helpers/accordion/Accordion';
import Cookies from 'js-cookie';

const Schedule = () => {
  const [scheduleData, setScheduleData] = useState<DriverSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStartDate, setCurrentStartDate] = useState<string>('');
  const [currentEndDate, setCurrentEndDate] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<string>('');

  // Filter states
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [driverOptions, setDriverOptions] = useState<DriverOption[]>([]);
  const [dispatcherOptions, setDispatcherOptions] = useState<DispatchOption[]>(
    [],
  );
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [selectedDispatcherIds, setSelectedDispatcherIds] = useState<number[]>(
    GetDispatchIds(),
  );

  function GetDispatchIds() {
    const savedDispatchIds = Cookies.get('dispatchers');
    return savedDispatchIds ? savedDispatchIds.split(',').map(Number) : [];
  }

  const GetScheduleData = useCallback(
    (
      startDate?: string,
      endDate?: string,
      driverIds?: number[],
      branchId?: number | null,
      preferredDispatchIds?: number[],
      selectedDriverId?: number | null,
    ) => {
      setIsLoading(true);

      //if a single driver is selected, use only that driver ID
      const finalDriverIds = selectedDriverId ? [selectedDriverId] : driverIds;

      const queryString = buildScheduleQueryString({
        startDate,
        endDate,
        driverIds: finalDriverIds,
        branchId: branchId || undefined,
        preferredDispatchIds,
      });

      const url = queryString
        ? `Schedule/GetDriverSchedule?${queryString}`
        : 'Schedule/GetDriverSchedule';

      Get(url)
        .then(res => {
          if (res) {
            setScheduleData(res.data || []);
          }
        })
        .catch(error => {
          console.error('Error fetching schedule data:', error);
          setScheduleData([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  useEffect(() => {
    const { startDate, endDate } = getCurrentWeekDates();
    setCurrentStartDate(startDate);
    setCurrentEndDate(endDate);
    setCustomStartDate(startDate);
    GetScheduleData(
      startDate,
      endDate,
      null,
      selectedBranchId,
      selectedDispatcherIds,
      selectedDriverId,
    );
  }, [
    GetScheduleData,
    selectedBranchId,
    selectedDispatcherIds,
    selectedDriverId,
  ]);

  // Load filter options
  useEffect(() => {
    Get('Load/GetBranchList').then(res => {
      if (res) {
        setBranchOptions(res.data);
      }
    });
    Get('Employee/GetDispatchOptions').then(res => {
      if (res) {
        setDispatcherOptions(res.data);
      }
    });
    Get('Employee/GetDriverOptions').then(res => {
      if (res) {
        setDriverOptions(res.data);
      }
    });
  }, []);

  const navigateWeek = (direction: 'prev' | 'next') => {
    // Parse the current start date using the same method as scheduleHelpers
    const [year, month, day] = currentStartDate.split('-').map(Number);
    const currentStart = new Date(year, month - 1, day); // month is 0-indexed

    const daysToAdd = direction === 'next' ? 7 : -7;

    // Add/subtract 7 days to get the new week start
    const newStartDate = new Date(
      currentStart.getFullYear(),
      currentStart.getMonth(),
      currentStart.getDate() + daysToAdd,
    );

    // Calculate the end date (6 days after start)
    const newEndDate = new Date(
      newStartDate.getFullYear(),
      newStartDate.getMonth(),
      newStartDate.getDate() + 6,
    );

    const formattedStartDate = formatDate(newStartDate);
    const formattedEndDate = formatDate(newEndDate);

    setCurrentStartDate(formattedStartDate);
    setCurrentEndDate(formattedEndDate);
    // Also update custom date inputs to stay in sync
    setCustomStartDate(formattedStartDate);
    GetScheduleData(
      formattedStartDate,
      formattedEndDate,
      undefined,
      selectedBranchId,
      selectedDispatcherIds,
      selectedDriverId,
    );
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    // Parse the current start date using the same method as scheduleHelpers
    const [year, month, day] = currentStartDate.split('-').map(Number);
    const currentStart = new Date(year, month - 1, day); // month is 0-indexed

    const daysToAdd = direction === 'next' ? 1 : -1;

    // Add/subtract 1 day to shift the 7-day window
    const newStartDate = new Date(
      currentStart.getFullYear(),
      currentStart.getMonth(),
      currentStart.getDate() + daysToAdd,
    );

    // Calculate the end date (6 days after start)
    const newEndDate = new Date(
      newStartDate.getFullYear(),
      newStartDate.getMonth(),
      newStartDate.getDate() + 6,
    );

    const formattedStartDate = formatDate(newStartDate);
    const formattedEndDate = formatDate(newEndDate);

    setCurrentStartDate(formattedStartDate);
    setCurrentEndDate(formattedEndDate);
    // Also update custom date inputs to stay in sync
    setCustomStartDate(formattedStartDate);
    GetScheduleData(
      formattedStartDate,
      formattedEndDate,
      undefined,
      selectedBranchId,
      selectedDispatcherIds,
      selectedDriverId,
    );
  };

  const goToCurrentWeek = () => {
    const { startDate, endDate } = getCurrentWeekDates();
    setCurrentStartDate(startDate);
    setCurrentEndDate(endDate);
    setCustomStartDate(startDate);
    GetScheduleData(
      startDate,
      endDate,
      undefined,
      selectedBranchId,
      selectedDispatcherIds,
      selectedDriverId,
    );
  };

  const getSundayToSaturdayFromDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const inputDate = new Date(year, month - 1, day);
    const dayOfWeek = inputDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    const sundayOfGivenDate = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate() - dayOfWeek,
    );

    const saturdayOfGivenDate = new Date(
      sundayOfGivenDate.getFullYear(),
      sundayOfGivenDate.getMonth(),
      sundayOfGivenDate.getDate() + 6,
    );

    return {
      startDate: formatDate(sundayOfGivenDate),
      endDate: formatDate(saturdayOfGivenDate),
    };
  };

  if (isLoading) {
    return <PageLoadingSpinner />;
  }

  const applyCustomDateRange = () => {
    if (customStartDate) {
      // Parse the custom start date
      const [year, month, day] = customStartDate.split('-').map(Number);
      const startDateObj = new Date(year, month - 1, day);

      // Calculate end date as start + 6 days (7-day range)
      const endDateObj = new Date(
        startDateObj.getFullYear(),
        startDateObj.getMonth(),
        startDateObj.getDate() + 6,
      );

      const startDate = formatDate(startDateObj);
      const endDate = formatDate(endDateObj);

      setCurrentStartDate(startDate);
      setCurrentEndDate(endDate);
      setCustomStartDate(startDate);
      GetScheduleData(
        startDate,
        endDate,
        undefined,
        selectedBranchId,
        selectedDispatcherIds,
        selectedDriverId,
      );
    }
  };

  const resetToCurrentWeek = () => {
    const { startDate } = getCurrentWeekDates();
    setCustomStartDate(startDate);
    goToCurrentWeek();
  };

  const handleBranchChange = (branchId: number | null) => {
    setSelectedBranchId(branchId);
    GetScheduleData(
      currentStartDate,
      currentEndDate,
      undefined,
      branchId,
      selectedDispatcherIds,
      selectedDriverId,
    );
  };

  const handleDriverChange = (driverId: number | null) => {
    setSelectedDriverId(driverId);
    GetScheduleData(
      currentStartDate,
      currentEndDate,
      undefined,
      selectedBranchId,
      selectedDispatcherIds,
      driverId,
    );
  };

  const handleDispatcherChange = (dispatcherIds: number[]) => {
    setSelectedDispatcherIds(dispatcherIds);
    GetScheduleData(
      currentStartDate,
      currentEndDate,
      undefined,
      selectedBranchId,
      dispatcherIds,
      selectedDriverId,
    );
  };

  const toggleDispatcher = (dispatcherId: number) => {
    const newSelectedIds = selectedDispatcherIds.includes(dispatcherId)
      ? selectedDispatcherIds.filter(id => id !== dispatcherId)
      : [...selectedDispatcherIds, dispatcherId];
    handleDispatcherChange(newSelectedIds);
  };

  const formatDisplayDate = (dateString: string): string => {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  const hasActiveFilters =
    selectedBranchId > 0 ||
    selectedDispatcherIds.length > 0 ||
    selectedDriverId > 0;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h1 className={classes.title}>Driver Schedule</h1>

        {/* Week Navigation */}
        <div className={classes.navigationContainer}>
          {/* Week Navigation */}
          <button
            onClick={() => navigateWeek('prev')}
            className={classes.navButton}
            disabled={isLoading}
          >
            ⟸ Previous Week
          </button>
          <button
            onClick={() => navigateDay('prev')}
            className={classes.navButton}
            disabled={isLoading}
          >
            ← Previous Day
          </button>
          <button
            onClick={goToCurrentWeek}
            className={`${classes.navButton} ${classes.primary}`}
            disabled={isLoading}
          >
            Current Week
          </button>
          <button
            onClick={() => navigateDay('next')}
            className={classes.navButton}
            disabled={isLoading}
          >
            Next Day →
          </button>
          <button
            onClick={() => navigateWeek('next')}
            className={classes.navButton}
            disabled={isLoading}
          >
            Next Week ⟹
          </button>

          <span className={classes.dateRange}>
            {currentStartDate &&
              currentEndDate &&
              `${formatDisplayDate(currentStartDate)} to ${formatDisplayDate(
                currentEndDate,
              )}`}
          </span>
        </div>

        {/* Custom Date Range */}
        <div className={classes.customDateContainer}>
          <label className={classes.customDateLabel}>Go to Date:</label>

          <div className={classes.dateInputGroup}>
            <label className={classes.dateInputLabel}>Select any date:</label>
            <input
              type="date"
              value={customStartDate}
              onChange={e => setCustomStartDate(e.target.value)}
              className={classes.dateInput}
            />
            <span className={classes.weekHelper}>(7-day range)</span>
          </div>

          <button
            onClick={applyCustomDateRange}
            className={`${classes.navButton} ${classes.primary}`}
            disabled={isLoading || !customStartDate}
          >
            Go to Week
          </button>

          <button
            onClick={resetToCurrentWeek}
            className={classes.navButton}
            disabled={isLoading}
          >
            Current Week
          </button>
        </div>

        {/* Filters */}
        <div className={classes.filtersWrapper}>
          <Accordion
            title={<span className={classes.filterTitle}>Filters</span>}
            defaultActive={hasActiveFilters}
          >
            <div className={classes.filtersContent}>
              <div className={classes.filterGroup}>
                <label className={classes.filterLabel}>Branch:</label>
                <select
                  value={selectedBranchId || ''}
                  onChange={e =>
                    handleBranchChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className={classes.filterSelect}
                >
                  <option value="">All Branches</option>
                  <OptionList
                    optionData={branchOptions}
                    attributeID="branchId"
                    attributeName="branchName"
                  />
                </select>
              </div>

              <div className={classes.filterGroup}>
                <label className={classes.filterLabel}>Driver:</label>
                <select
                  value={selectedDriverId || ''}
                  onChange={e =>
                    handleDriverChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className={classes.filterSelect}
                >
                  <option value="">All Drivers</option>
                  <OptionList
                    optionData={driverOptions}
                    attributeID="driverId"
                    attributeName="name"
                  />
                </select>
              </div>

              <div className={classes.filterGroup}>
                <label className={classes.filterLabel}>Dispatchers:</label>
                <div className={classes.multiSelectContainer}>
                  {dispatcherOptions.map(dispatcher => (
                    <label
                      key={dispatcher.dispatchId}
                      className={classes.checkboxLabel}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDispatcherIds.includes(
                          dispatcher.dispatchId,
                        )}
                        onChange={() => toggleDispatcher(dispatcher.dispatchId)}
                        className={classes.checkbox}
                      />
                      <span className={classes.checkboxText}>
                        {dispatcher.dispatchName}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Accordion>
        </div>
      </div>

      {scheduleData.length > 0 ? (
        <WeeklyCalendar
          driverSchedules={scheduleData}
          startDate={currentStartDate}
        />
      ) : (
        <div className={classes.emptyState}>
          <p>No schedule data available for the selected date range.</p>
        </div>
      )}
    </div>
  );
};

export default Schedule;
